import React, { useState, useEffect } from 'react';
import { useAuth } from '../context/AuthContext';
import { Card, CardContent, CardHeader, CardTitle } from './ui/card';
import { Button } from './ui/button';
import { Textarea } from './ui/textarea';
import { Label } from './ui/label';
import { Badge } from './ui/badge';
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogFooter, DialogDescription } from './ui/dialog';
import { ClipboardCheck, Star, Send, CheckCircle2, MessageSquare, ThumbsUp, AlertCircle } from 'lucide-react';
import { toast } from 'sonner';
import api from '../services/api';

const QuestionnaireSatisfaction = () => {
  const { user } = useAuth();
  const [showDialog, setShowDialog] = useState(false);
  const [submitted, setSubmitted] = useState(false);
  const [loading, setLoading] = useState(false);
  const [previousResponses, setPreviousResponses] = useState([]);
  const [loadingResponses, setLoadingResponses] = useState(true);

  const [formData, setFormData] = useState({
    satisfaction_globale: 0,
    qualite_accompagnement: 0,
    reactivite_equipe: 0,
    outils_plateforme: 0,
    communication: 0,
    points_positifs: '',
    points_amelioration: '',
    suggestions: '',
    recommandation: 0
  });

  const questions = [
    { key: 'satisfaction_globale', label: 'Satisfaction globale de la plateforme', icon: Star },
    { key: 'qualite_accompagnement', label: 'Qualité de l\'accompagnement des bénéficiaires', icon: ThumbsUp },
    { key: 'reactivite_equipe', label: 'Réactivité de l\'équipe', icon: MessageSquare },
    { key: 'outils_plateforme', label: 'Ergonomie et fonctionnalités de la plateforme', icon: ClipboardCheck },
    { key: 'communication', label: 'Qualité de la communication', icon: MessageSquare },
  ];

  useEffect(() => {
    loadPreviousResponses();
  }, []);

  const loadPreviousResponses = async () => {
    try {
      const response = await api.get('/satisfaction/mes-reponses');
      setPreviousResponses(response.data || []);
    } catch (error) {
      console.warn('Erreur chargement réponses précédentes:', error);
    } finally {
      setLoadingResponses(false);
    }
  };

  const handleRating = (key, value) => {
    setFormData(prev => ({ ...prev, [key]: value }));
  };

  const handleSubmit = async () => {
    // Vérifier que toutes les notes sont remplies
    const allRated = questions.every(q => formData[q.key] > 0) && formData.recommandation > 0;
    if (!allRated) {
      toast.error('Veuillez répondre à toutes les questions de notation');
      return;
    }

    setLoading(true);
    try {
      await api.post('/satisfaction', {
        ...formData,
        user_role: user?.role
      });
      toast.success('Merci pour votre retour !');
      setSubmitted(true);
      setShowDialog(false);
      loadPreviousResponses();
      // Reset form
      setFormData({
        satisfaction_globale: 0,
        qualite_accompagnement: 0,
        reactivite_equipe: 0,
        outils_plateforme: 0,
        communication: 0,
        points_positifs: '',
        points_amelioration: '',
        suggestions: '',
        recommandation: 0
      });
    } catch (error) {
      toast.error('Erreur lors de l\'envoi du questionnaire');
    } finally {
      setLoading(false);
    }
  };

  const StarRating = ({ value, onChange, label }) => (
    <div className="space-y-2">
      <Label className="text-sm font-medium text-slate-700">{label}</Label>
      <div className="flex gap-1">
        {[1, 2, 3, 4, 5].map((star) => (
          <button
            key={star}
            type="button"
            onClick={() => onChange(star)}
            className={`p-1 transition-all ${
              star <= value 
                ? 'text-amber-400 scale-110' 
                : 'text-slate-300 hover:text-amber-200'
            }`}
          >
            <Star className="w-8 h-8" fill={star <= value ? 'currentColor' : 'none'} />
          </button>
        ))}
        <span className="ml-2 text-sm text-slate-500 self-center">
          {value > 0 ? `${value}/5` : ''}
        </span>
      </div>
    </div>
  );

  const getAverageScore = (response) => {
    const scores = [
      response.satisfaction_globale,
      response.qualite_accompagnement,
      response.reactivite_equipe,
      response.outils_plateforme,
      response.communication
    ].filter(s => s > 0);
    return scores.length > 0 ? (scores.reduce((a, b) => a + b, 0) / scores.length).toFixed(1) : 0;
  };

  return (
    <div className="space-y-6">
      {/* Carte principale */}
      <Card className="bg-gradient-to-r from-purple-50 to-indigo-50 rounded-3xl shadow-sm border-0">
        <CardHeader className="pb-2">
          <CardTitle className="text-xl font-bold flex items-center gap-2" style={{ fontFamily: 'Nunito, sans-serif' }}>
            <ClipboardCheck className="w-6 h-6 text-purple-600" />
            Questionnaire de Satisfaction
          </CardTitle>
        </CardHeader>
        <CardContent>
          <p className="text-slate-600 mb-4">
            Votre avis est important pour nous aider à améliorer nos services.
          </p>
          <Button 
            onClick={() => setShowDialog(true)}
            className="bg-purple-600 hover:bg-purple-700 rounded-xl"
            data-testid="open-satisfaction-survey"
          >
            <MessageSquare className="w-4 h-4 mr-2" />
            Donner mon avis
          </Button>
        </CardContent>
      </Card>

      {/* Historique des réponses */}
      {!loadingResponses && previousResponses.length > 0 && (
        <Card className="bg-white rounded-3xl shadow-sm">
          <CardHeader>
            <CardTitle className="text-lg font-bold flex items-center gap-2">
              <CheckCircle2 className="w-5 h-5 text-green-600" />
              Mes avis précédents
            </CardTitle>
          </CardHeader>
          <CardContent>
            <div className="space-y-3">
              {previousResponses.slice(0, 3).map((response, index) => (
                <div key={response.id || index} className="bg-slate-50 rounded-xl p-4 border border-slate-200">
                  <div className="flex items-center justify-between">
                    <div className="flex items-center gap-2">
                      <Badge className="bg-purple-100 text-purple-700">
                        Note moyenne: {getAverageScore(response)}/5
                      </Badge>
                      <Badge variant="outline">
                        Recommandation: {response.recommandation}/10
                      </Badge>
                    </div>
                    <span className="text-xs text-slate-500">
                      {new Date(response.created_at).toLocaleDateString('fr-FR')}
                    </span>
                  </div>
                  {response.suggestions && (
                    <p className="text-sm text-slate-600 mt-2 italic">
                      "{response.suggestions.substring(0, 100)}..."
                    </p>
                  )}
                </div>
              ))}
            </div>
          </CardContent>
        </Card>
      )}

      {/* Dialog du questionnaire */}
      <Dialog open={showDialog} onOpenChange={setShowDialog}>
        <DialogContent className="sm:max-w-2xl rounded-3xl max-h-[90vh] overflow-y-auto">
          <DialogHeader>
            <DialogTitle className="flex items-center gap-2 text-xl">
              <ClipboardCheck className="w-6 h-6 text-purple-600" />
              Questionnaire de Satisfaction
            </DialogTitle>
            <DialogDescription>
              Merci de prendre quelques minutes pour nous donner votre avis.
            </DialogDescription>
          </DialogHeader>

          <div className="space-y-6 py-4">
            {/* Questions de notation */}
            <div className="space-y-4">
              <h3 className="font-semibold text-slate-900 flex items-center gap-2">
                <Star className="w-5 h-5 text-amber-500" />
                Évaluez les aspects suivants (1 à 5 étoiles)
              </h3>
              {questions.map((q) => (
                <StarRating
                  key={q.key}
                  label={q.label}
                  value={formData[q.key]}
                  onChange={(val) => handleRating(q.key, val)}
                />
              ))}
            </div>

            {/* Recommandation NPS */}
            <div className="border-t pt-4">
              <Label className="text-sm font-medium text-slate-700">
                Sur une échelle de 1 à 10, recommanderiez-vous cette plateforme ?
              </Label>
              <div className="flex gap-1 mt-2">
                {[1, 2, 3, 4, 5, 6, 7, 8, 9, 10].map((num) => (
                  <button
                    key={num}
                    type="button"
                    onClick={() => handleRating('recommandation', num)}
                    className={`w-9 h-9 rounded-lg text-sm font-bold transition-all ${
                      num === formData.recommandation
                        ? 'bg-purple-600 text-white scale-110'
                        : num <= 6
                          ? 'bg-red-100 text-red-700 hover:bg-red-200'
                          : num <= 8
                            ? 'bg-amber-100 text-amber-700 hover:bg-amber-200'
                            : 'bg-green-100 text-green-700 hover:bg-green-200'
                    }`}
                  >
                    {num}
                  </button>
                ))}
              </div>
              <div className="flex justify-between text-xs text-slate-500 mt-1">
                <span>Pas du tout</span>
                <span>Absolument</span>
              </div>
            </div>

            {/* Questions ouvertes */}
            <div className="space-y-4 border-t pt-4">
              <div>
                <Label htmlFor="points_positifs">Points positifs</Label>
                <Textarea
                  id="points_positifs"
                  value={formData.points_positifs}
                  onChange={(e) => setFormData(prev => ({ ...prev, points_positifs: e.target.value }))}
                  placeholder="Qu'appréciez-vous particulièrement ?"
                  className="mt-1 rounded-xl"
                  rows={2}
                />
              </div>
              <div>
                <Label htmlFor="points_amelioration">Points d'amélioration</Label>
                <Textarea
                  id="points_amelioration"
                  value={formData.points_amelioration}
                  onChange={(e) => setFormData(prev => ({ ...prev, points_amelioration: e.target.value }))}
                  placeholder="Que pourrions-nous améliorer ?"
                  className="mt-1 rounded-xl"
                  rows={2}
                />
              </div>
              <div>
                <Label htmlFor="suggestions">Suggestions ou commentaires</Label>
                <Textarea
                  id="suggestions"
                  value={formData.suggestions}
                  onChange={(e) => setFormData(prev => ({ ...prev, suggestions: e.target.value }))}
                  placeholder="Avez-vous des suggestions ?"
                  className="mt-1 rounded-xl"
                  rows={2}
                />
              </div>
            </div>
          </div>

          <DialogFooter className="gap-2">
            <Button variant="outline" onClick={() => setShowDialog(false)} className="rounded-xl">
              Annuler
            </Button>
            <Button 
              onClick={handleSubmit} 
              disabled={loading}
              className="bg-purple-600 hover:bg-purple-700 rounded-xl"
              data-testid="submit-satisfaction"
            >
              {loading ? (
                'Envoi en cours...'
              ) : (
                <>
                  <Send className="w-4 h-4 mr-2" />
                  Envoyer mon avis
                </>
              )}
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </div>
  );
};

export default QuestionnaireSatisfaction;
